/**
 * DockablePanel — the shared shell behind Add / Format / Media / Layout.
 *
 * All four panels ran identical copies of the same ~45 lines: read the dock,
 * gate on THIS panel's membership, wire a drag handle to hitTest/drop, publish
 * a rect for hit-testing, and animate the tear-off/ungroup transitions.
 * MediaLibraryPanel's own comment admitted it "mirrors DesktopFormatPanel
 * exactly". Four copies of stateful dock logic is four places for it to drift.
 *
 * This file splits that into two layers, because the four panels genuinely
 * agree on behaviour and genuinely disagree on markup:
 *
 * - `useDockablePanel` — the logic. Adopted by all four.
 * - `DockablePanel` — the visual chrome (grip header, collapse, drop preview).
 *   Adopted by Media and Layout only; Add and Format keep their own chrome.
 */
import { useEffect, useLayoutEffect, useState } from "react";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";

import { DockDropPreview } from "./DockDropPreview.js";
import { glideFrom, settle } from "./panelAnim.js";
import { panelDock, usePanelDock, type DockTab } from "./panelDock.js";
import { useDraggablePanel } from "./useDraggablePanel.js";
import { EditorButton } from "./ui/EditorButton.js";

export interface DockablePanelOptions {
  /**
   * Add renders `position: absolute` inside the canvas container rather than
   * fixed to the viewport, so its tear-off drop point — which arrives in
   * VIEWPORT coords — has to be converted through the offsetParent. The other
   * three are fixed, where viewport coords are already correct.
   */
  readonly relativeToOffsetParent?: boolean;
}

export interface DockablePanelState {
  /** Merged into the tabbed group → render nothing; the group renders the body. */
  readonly grouped: boolean;
  /** Another panel is hovering this one as a dock target. */
  readonly docking: boolean;
  /** This panel is the front-most one (z-order). */
  readonly atFront: boolean;
  readonly ref: ReturnType<typeof useDraggablePanel>["ref"];
  readonly pos: ReturnType<typeof useDraggablePanel>["pos"];
  /** Attach to the drag handle's `onPointerDown`. */
  readonly onHandleDown: ReturnType<typeof useDraggablePanel>["onHandleDown"];
  /** Attach to the panel root's `onPointerDown`. */
  readonly bringToFront: () => void;
}

export function useDockablePanel(
  tab: DockTab,
  initial: { x: number; y: number },
  options: DockablePanelOptions = {},
): DockablePanelState {
  const { candidate, front, members, ungroupFrom, ungroupSeq } = usePanelDock();
  // Gate on THIS panel's membership, not the global `grouped`: a group of the
  // other panels can exist while this one is still standalone.
  const grouped = members.includes(tab);
  const { ref, pos, setPos, onHandleDown } = useDraggablePanel(initial, {
    onMove: (pt) => panelDock.hitTest(tab, pt.x, pt.y),
    onDrop: () => panelDock.drop(tab),
  });
  useEffect(
    () =>
      panelDock.registerRect(
        tab,
        () => ref.current?.getBoundingClientRect() ?? null,
      ),
    [ref, tab],
  );
  // On tear-off: settle under the pointer. On ungroup: glide home from the
  // group's old spot. Layout effect so the position lands before paint (no
  // flash) and the tween starts from the right place.
  useLayoutEffect(() => {
    if (grouped) return;
    const el = ref.current;
    if (!el) return;
    const drop = panelDock.consumeTearRestore(tab);
    if (drop) {
      const origin = options.relativeToOffsetParent
        ? (el.offsetParent as HTMLElement | null)?.getBoundingClientRect()
        : null;
      setPos({
        x: Math.max(0, drop.x - (origin?.left ?? 0) - 40),
        y: Math.max(0, drop.y - (origin?.top ?? 0) - 8),
      });
      settle(el);
    } else if (ungroupFrom) {
      glideFrom(el, ungroupFrom);
    }
    // ungroupSeq makes each ungroup re-fire; `grouped` gates it to the
    // un-grouped side. setPos/ref are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, ungroupSeq]);

  return {
    grouped,
    docking: candidate === tab,
    atFront: front === tab,
    ref,
    pos,
    onHandleDown,
    bringToFront: () => panelDock.bringToFront(tab),
  };
}

export interface DockablePanelProps {
  readonly tab: DockTab;
  /** Header text. Kept explicit rather than read from TAB_LABEL so a panel can
   *  title itself differently from its tab if it ever needs to. */
  readonly title: string;
  readonly initial: { x: number; y: number };
  /** The panel's width/height (px). The height is dropped while collapsed. */
  readonly style?: { readonly width?: number; readonly height?: number };
  /** Draws the focus ring — e.g. Media while it is armed for a pick. */
  readonly armed?: boolean;
  /** Buttons rendered in the header before the collapse chevron. */
  readonly headerExtras?: React.ReactNode;
  /** Rendered below the body when open — e.g. Media's resize grip. */
  readonly footer?: React.ReactNode;
  /** Controlled collapse. Omit both to let the panel own the state. */
  readonly collapsed?: boolean;
  readonly onCollapsedChange?: (collapsed: boolean) => void;
  readonly children: React.ReactNode;
}

export function DockablePanel({
  tab,
  title,
  initial,
  style,
  armed = false,
  headerExtras,
  footer,
  collapsed,
  onCollapsedChange,
  children,
}: DockablePanelProps): React.ReactElement | null {
  const { grouped, docking, atFront, ref, pos, onHandleDown, bringToFront } =
    useDockablePanel(tab, initial);
  const [ownCollapsed, setOwnCollapsed] = useState(false);
  const isCollapsed = collapsed ?? ownCollapsed;
  const toggle = () => {
    const next = !isCollapsed;
    setOwnCollapsed(next);
    onCollapsedChange?.(next);
  };

  if (grouped) return null;

  return (
    <html.div
      ref={ref}
      onPointerDown={bringToFront}
      style={[
        s.panel,
        (armed || docking) && s.panelArmed,
        style?.width != null && s.width(style.width),
        !isCollapsed && style?.height != null && s.height(style.height),
        s.at(pos.x, pos.y),
        atFront ? s.front : s.back,
      ]}
    >
      {docking ? (
        <DockDropPreview me={tab} />
      ) : (
        <html.div onPointerDown={onHandleDown} style={s.header}>
          <html.span aria-hidden style={s.grip}>
            ⠿
          </html.span>
          <html.span>{title}</html.span>
          <html.div style={s.headerEnd}>
            {headerExtras}
            <EditorButton
              aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
              aria-expanded={!isCollapsed}
              onClick={toggle}
              isolatePress
            >
              {isCollapsed ? "▸" : "▾"}
            </EditorButton>
          </html.div>
        </html.div>
      )}
      {!isCollapsed && !docking && children}
      {!isCollapsed && !docking && footer}
    </html.div>
  );
}

const s = css.create({
  panel: {
    position: "fixed",
    backgroundColor: colors.bgCard,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.md,
    boxShadow: shadows.lg,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Armed (Media waiting for a pick) or a dock target: the accent ring.
  panelArmed: {
    outlineWidth: borders.focusRing,
    outlineStyle: "solid",
    outlineColor: colors.accent,
  },
  width: (width: number) => ({ width }),
  height: (height: number) => ({ height }),
  at: (left: number, top: number) => ({ left, top }),
  front: { zIndex: layers.modal },
  back: { zIndex: layers.overlay },
  header: {
    display: "flex",
    alignItems: "center",
    gap: microSpacing.px6,
    paddingTop: microSpacing.px6,
    paddingRight: spacing.sm,
    paddingBottom: microSpacing.px6,
    paddingLeft: spacing.ms,
    backgroundColor: colors.bgCardHover,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    cursor: "move",
    touchAction: "none",
    userSelect: "none",
    fontSize: typeScale.fontSize2,
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
    flexShrink: 0,
  },
  // Pushes the extras + collapse chevron to the header's far end.
  headerEnd: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: microSpacing.px6,
  },
  grip: {
    color: colors.border,
    fontSize: typography.fontSizeXs,
    letterSpacing: "0.1em",
  },
});
